import React from 'react';
import { motion } from 'framer-motion';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'shimmer' | 'none';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'rounded',
  width,
  height,
  animation = 'shimmer'
}) => {
  const baseClasses = 'bg-gray-200 dark:bg-gray-700';
  
  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-lg'
  };

  const animationClasses = {
    pulse: 'animate-pulse',
    shimmer: 'skeleton-shimmer',
    none: ''
  };

  const style: React.CSSProperties = {
    width: width || (variant === 'text' ? '100%' : undefined),
    height: height || (variant === 'text' ? '1em' : undefined)
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`
        ${baseClasses} 
        ${variantClasses[variant]} 
        ${animationClasses[animation]}
        ${className}
      `}
      style={style}
    />
  );
};

// Skeleton Card Component
export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`card p-4 space-y-4 ${className}`}>
    <Skeleton variant="rounded" height={200} />
    <Skeleton variant="text" width="70%" />
    <Skeleton variant="text" width="50%" />
    <div className="flex gap-2">
      <Skeleton variant="rounded" width={80} height={32} />
      <Skeleton variant="rounded" width={80} height={32} />
    </div>
  </div>
);

// Skeleton Grid
export const SkeletonGrid: React.FC<{ 
  count?: number; 
  columns?: number;
  className?: string;
}> = ({ count = 6, columns = 3, className = '' }) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  return (
    <div className={`grid ${gridCols[columns as keyof typeof gridCols] || gridCols[3]} gap-6 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.1 }}
        >
          <SkeletonCard />
        </motion.div>
      ))}
    </div>
  );
};

// Page Skeleton Loader
export const PageSkeleton: React.FC = () => (
  <div className="min-h-screen pt-24 pb-16">
    {/* Hero Skeleton */}
    <div className="h-[60vh] bg-gray-200 dark:bg-gray-800 skeleton-shimmer" />
    
    {/* Content Skeleton */}
    <div className="container-custom py-16">
      <div className="flex justify-center mb-12">
        <Skeleton variant="text" width={300} height={40} />
      </div>
      <SkeletonGrid count={6} columns={3} />
    </div>
  </div>
);

export default Skeleton;
